import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ChevronLeft, Copy, Fuel, Sparkles, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * /abastecer — tela mostrada quando MODULO_COMBUSTIVEL esta ativo pro tenant.
 * Hero exibe o CPF formatado (codigo de fidelidade que o cliente passa ao
 * frentista). Backend recebe via AutoSystem posvenda e credita pontos.
 *
 * Sprint 11.
 */

interface ClienteRow {
  seq_cliente: number;
  nom_cliente: string;
  num_cpf_cnpj: string | null;
}

interface MesPagamentos {
  pontosAcumulados: number;
  qtdPagamentos: number;
}

function formatarCpf(cpfDigits: string | null): string {
  if (!cpfDigits) return '—';
  const d = cpfDigits.replace(/\D/g, '');
  if (d.length !== 11) return d;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function AbastecerPage() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  const cliente = useQuery({
    queryKey: ['cliente-abastecer', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_clientes')
        .select('seq_cliente, nom_cliente, num_cpf_cnpj')
        .eq('cod_auth_user', user!.id)
        .single();
      if (error) throw error;
      return data as ClienteRow;
    },
    enabled: !!user,
  });

  // Pontos creditados nos ultimos 30 dias por cod_origem=PAGAMENTO (combustivel)
  const pontosMes = useQuery({
    queryKey: ['abastecer-pontos-mes', cliente.data?.seq_cliente],
    queryFn: async (): Promise<MesPagamentos> => {
      const desde = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('tab_pontos_fidelidade')
        .select('qtd_pontos')
        .eq('seq_cliente', cliente.data!.seq_cliente)
        .eq('cod_origem', 'PAGAMENTO')
        .gte('dta_lancamento', desde);
      if (error) throw error;
      const lista = (data ?? []) as Array<{ qtd_pontos: number }>;
      return {
        pontosAcumulados: lista.reduce((s, r) => s + (r.qtd_pontos ?? 0), 0),
        qtdPagamentos: lista.length,
      };
    },
    enabled: !!cliente.data?.seq_cliente,
  });

  function copiarCpf() {
    if (!cliente.data?.num_cpf_cnpj) return;
    void navigator.clipboard.writeText(cliente.data.num_cpf_cnpj.replace(/\D/g, ''));
    toast.success('CPF copiado.');
  }

  const cpfFormatado = formatarCpf(cliente.data?.num_cpf_cnpj ?? null);

  return (
    <div className="space-y-5 p-4 pb-24">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground -ml-2 h-8 px-2">
          <Link to="/" aria-label="Voltar para a home">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Voltar
          </Link>
        </Button>
      </div>

      <header className="space-y-1">
        <h1 className="text-display-md font-extrabold tracking-tight">Abastecer</h1>
        <p className="text-muted-foreground text-sm">
          Mostre seu CPF ao frentista antes do abastecimento. {tenant?.nom_tenant ?? 'Octa Pontos'}{' '}
          credita pontos automaticamente apos a NFe.
        </p>
      </header>

      {/* Hero — CPF gigante (codigo de fidelidade) */}
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card className="bg-octa-gradient relative overflow-hidden rounded-3xl text-white">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/15 blur-3xl"
          />
          <CardContent className="relative z-10 space-y-3 p-6 text-center">
            <div className="flex items-center justify-center gap-2">
              <Fuel className="h-5 w-5" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                Codigo de fidelidade
              </span>
            </div>
            {cliente.isLoading ? (
              <Skeleton className="mx-auto h-12 w-64 bg-white/20" />
            ) : (
              <p className="text-3xl font-extrabold tabular-nums tracking-wider sm:text-4xl">
                {cpfFormatado}
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={copiarCpf}
              disabled={!cliente.data?.num_cpf_cnpj}
              className="bg-white/20 text-white hover:bg-white/30"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copiar CPF
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Pontos do mes */}
      <Card variant="glass" className="rounded-2xl">
        <CardContent className="space-y-2 p-5">
          <div className="flex items-center gap-2">
            <TrendingUp className="text-primary h-4 w-4" />
            <span className="text-overline text-muted-foreground uppercase">
              Pontos por abastecimento — ultimos 30 dias
            </span>
          </div>
          {pontosMes.isLoading ? (
            <Skeleton className="h-10 w-32" />
          ) : (
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-extrabold tabular-nums">
                {pontosMes.data?.pontosAcumulados ?? 0}
              </span>
              <span className="text-muted-foreground text-sm">
                pts em {pontosMes.data?.qtdPagamentos ?? 0} abastecimento(s)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Como funciona */}
      <Card variant="elevated" className="rounded-2xl">
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary h-4 w-4" />
            <span className="text-overline text-muted-foreground uppercase">Como funciona</span>
          </div>
          <ol className="space-y-3 text-sm">
            {[
              'Antes de abastecer, mostre o CPF ao frentista (ou copie aqui).',
              'O frentista informa o seu CPF na bomba. Abasteca normalmente.',
              'Apos a NFe, em ate 10 minutos seus pontos caem no saldo.',
            ].map((txt, i) => (
              <li key={i} className="flex gap-3">
                <span className="bg-octa-gradient flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span>{txt}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
